/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */
import { Fragment, type ReactNode } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import CommentIcon from '@atlaskit/icon/core/comment';
import MentionIcon from '@atlaskit/icon/core/mention';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type {
	SearchPageNode,
	ThirdPartyRestrictionLevel,
} from '@atlassian/search-client/search-page/types';

import type { PrimaryProductKey } from '../../constants/product-keys';
import type { ThirdPartyConfigsBootstrap } from '../../constants/schemas/3p-config';
import type { ResultClickedCallback } from '../../constants/types';
import { EmbeddedComments, type EmbeddedComment } from '../embedded-comments';
import { Expand, ExpandList } from '../expand-list';
import {
	isSimilarLinkedResultsEligible,
	LinkedResults,
	SimilarLinkedResultsExpand,
} from '../linked-results';
import { messages as linkedResultsMessages } from '../linked-results/messages';
import { RestrictedLozenge } from '../restricted-lozenge';

const styles = cssMap({
	footerContainer: {
		paddingTop: token('space.100'),
	},
});

/**
 * Props passed by a search result renderer (e.g. SearchGraphDocument) to surface its three
 * "context" tabs in a single shared row of expand buttons:
 *   • Comments      — discussion contained inside the result
 *   • Mentioned in  — other documents that reference this result
 *   • Related links — semantically similar documents
 *
 * Rows are rendered when their bucket is non-empty and any product-specific eligibility is
 * satisfied. The first non-empty visible row is auto-expanded.
 */
export interface SearchResultContextFooterProps {
	/** Embedded comments already extracted from the result. */
	embeddedComments: EmbeddedComment[];
	/** Whether embedded comments are FF-enabled for the current product. */
	isEmbeddedCommentsEnabled: boolean;
	/**
	 * When true, force the Comments row to be auto-expanded. Set by callers when the user has
	 * explicitly selected the "Comments" type filter — their intent is to inspect comments,
	 * so the row should be open by default. Defaults to false; when omitted, Comments still
	 * auto-expands when it is the first non-empty visible row (its position by convention).
	 */
	forceCommentsExpanded?: boolean;
	/** Raw linkedResults array from the result; mixed categories. */
	linkedResults: SearchPageNode | undefined | null;
	/** Whether the current product is allowed to surface the "Mentioned in" row. */
	isMentionedInEnabled: boolean;
	/** Restriction level used to drive the empty-state lozenge. */
	permissionLevel: ThirdPartyRestrictionLevel | null;
	/** Whether the result is non-public; controls lozenge visibility. */
	isNotPublic: boolean;
	/** Click + context-menu handlers, already wrapped with thirdPartyId by the parent. */
	onResultClicked: ResultClickedCallback;
	onResultContextMenu?: ResultClickedCallback;
	/** Position of the parent result in the search results list (for analytics). */
	position?: number;
	/** Result identifiers passed through to LinkedResults inside the Mentioned-in row. */
	id: string;
	url: string;
	enrichedTitle: ReactNode;
	product?: string;
	selectedProduct?: string;
	primaryProduct?: PrimaryProductKey;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap | undefined | null;
	/** The relay fragment data for the result; passed to SimilarLinkedResultsExpand. */
	partial: SearchPageNode;
}

const MENTIONED_BY = 'MENTIONEDBY';

/**
 * Renders the three peer "context" tabs (Comments, Mentioned in, Related links) for a search
 * result as a single shared row of expand buttons. Clicking a button reveals its content beneath
 * the row; only one row is open at a time (enforced by `ExpandList`).
 *
 * Bucketing is purely category-driven:
 *   • `category === 'SIMILAR'`     → Related links
 *   • `category === 'MENTIONEDBY'` → Mentioned in
 *   • Other categories             → ignored (consistent with the previous behaviour for 3P
 *                                    document renderers)
 *
 * Rows are emitted in a fixed visual order: Comments → Mentioned in → Related links. The first
 * non-empty visible row is auto-expanded; subsequent rows remain collapsed until clicked. When
 * no rows are eligible the component falls back to the restricted-lozenge (if `isNotPublic`),
 * matching the prior renderer behaviour.
 */
export const SearchResultContextFooter = ({
	embeddedComments,
	isEmbeddedCommentsEnabled,
	forceCommentsExpanded = false,
	linkedResults,
	isMentionedInEnabled,
	permissionLevel,
	isNotPublic,
	onResultClicked,
	onResultContextMenu,
	position,
	id,
	url,
	enrichedTitle,
	product,
	selectedProduct,
	primaryProduct,
	thirdPartyConfigs,
	partial,
}: SearchResultContextFooterProps): JSX.Element | null => {
	const { formatMessage } = useIntl();

	const hasEmbeddedComments = isEmbeddedCommentsEnabled && embeddedComments.length > 0;

	// `linkedResults` is the Relay-generated SearchPageNode supertype. Each element exposes a
	// `category` field of type `string | null | undefined`; we treat it as a simple
	// case-insensitive match against the category constants.
	const safeLinkedResults: ReadonlyArray<{ readonly category?: string | null }> =
		linkedResults ?? [];
	const mentionedByLinkedResults = safeLinkedResults.filter(
		(linkedResult) => linkedResult.category?.toLocaleUpperCase() === MENTIONED_BY,
	);

	const isSimilarEligible = isSimilarLinkedResultsEligible({
		partial,
	});
	const hasSimilar =
		isSimilarEligible &&
		safeLinkedResults.some((linkedResult) => linkedResult.category?.toUpperCase() === 'SIMILAR');

	// Mentioned-in is suppressed whenever Related links is shown: by product convention only one
	// of the two ever renders (Related links takes priority when SIMILAR items exist). This keeps
	// the footer focused on the most useful "linked" surface for the result.
	const hasMentionedIn = isMentionedInEnabled && mentionedByLinkedResults.length > 0 && !hasSimilar;

	// Build rows in fixed order. The Comments row uses `attachmentCount={embeddedComments.length}`
	// purely so ExpandList does not filter it out (ExpandList drops children whose
	// attachmentCount is 0); the rendered button surface still shows only the label, since
	// Drive/SharePoint do not expose total comment counts.
	const commentsExpand = hasEmbeddedComments ? (
		<Expand
			icon={CommentIcon}
			attachmentCount={embeddedComments.length}
			attachmentType="comment"
			label={formatMessage(linkedResultsMessages.comments)}
		>
			{/* No wrapper Box: keep the DOM shape of the Comments expand body identical to
			    Mentioned in / Related links (each renders a single child directly under
			    Expand). EmbeddedComments owns its own internal padding/grid. */}
			<EmbeddedComments comments={embeddedComments} />
		</Expand>
	) : null;

	const mentionedInExpand = hasMentionedIn ? (
		<Expand
			icon={MentionIcon}
			attachmentCount={mentionedByLinkedResults.length}
			attachmentType="url"
			label={formatMessage(linkedResultsMessages.mentionedIn)}
		>
			<LinkedResults
				id={id}
				product={product}
				productSelected={!!selectedProduct}
				primaryProduct={primaryProduct}
				title={enrichedTitle}
				url={url}
				icon={<></>}
				thirdPartyConfigs={thirdPartyConfigs}
				linkedResults={mentionedByLinkedResults}
				onResultClicked={onResultClicked}
				onResultContextMenu={onResultContextMenu}
				position={position}
			/>
		</Expand>
	) : null;

	// SimilarLinkedResultsExpand returns an <Expand> element directly. We invoke it as a regular
	// function (not as a JSX element) so the resulting <Expand> sits directly underneath the
	// shared ExpandList — ExpandList introspects its direct children's props (icon, label,
	// attachmentCount) to render the row of buttons, so wrapping the row in another component
	// would hide those props from it.
	const relatedLinksExpand = hasSimilar
		? SimilarLinkedResultsExpand({
				linkedResults: safeLinkedResults,
				thirdPartyConfigs,
				partial,
				url,
				position,
				onResultClicked,
				onResultContextMenu,
				product,
			})
		: null;

	// Render rows in fixed visual order (Comments → Mentioned in → Related links). Null entries
	// are filtered out so ExpandList only sees the visible children directly (it introspects each
	// child's `icon` / `label` / `attachmentCount` props to render the row of buttons, so the
	// children must remain bare <Expand> elements rather than being wrapped in fragments).
	// `defaultExpandedIndex={0}` then auto-expands the first non-empty visible row.
	const visibleExpands: JSX.Element[] = [
		commentsExpand,
		mentionedInExpand,
		relatedLinksExpand,
	].filter((row): row is JSX.Element => row !== null);

	if (visibleExpands.length === 0) {
		// No tabs to render — fall back to the restricted lozenge for non-public results, matching
		// the renderer's previous footer behaviour.
		if (isNotPublic) {
			return (
				<Box xcss={styles.footerContainer}>
					<RestrictedLozenge restriction={permissionLevel} />
				</Box>
			);
		}
		return null;
	}

	// Comments auto-expand when present (matches the prior behaviour for embedded comments —
	// they are intrinsic content of the result and worth surfacing immediately). The two
	// "linked" surfaces (Mentioned in, Related links) start collapsed: only one of them is
	// ever rendered, by product convention, and the user opts in to inspect it. The
	// `forceCommentsExpanded` prop has no extra effect here — Comments is always the first
	// row in `visibleExpands` when present, and is therefore already at index 0 — but the
	// flag is honoured upstream by the document renderer (it bypasses the per-product
	// embedded-comments gate so the row renders at all when the Comments filter is active).
	const commentsIndex = visibleExpands.findIndex((expand) => expand === commentsExpand);
	const defaultExpandedIndex =
		commentsIndex >= 0 && (forceCommentsExpanded || commentsIndex === 0)
			? commentsIndex
			: undefined;

	return (
		<ExpandList
			parentPosition={position}
			buttonType="LINKED_COMMENT_FOOTER"
			defaultExpandedIndex={defaultExpandedIndex}
		>
			{visibleExpands}
		</ExpandList>
	);
};
